import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import { ApiRequestError } from './services/api';
import { trackPageTraces } from './services/requestTracing';

if (window.config.VITE_SENTRY_DSN) {
  // List of errors we don't want to pass to Sentry. To prevent excluding
  // similar errors the error message needs to match exactly.
  const errorsToExclude: string[] = [
    'tabsRef.current is null', // MUI Tabs related error, remove from list when use of MUI Tabs is removed
    "Cannot destructure property 'scrollTop' of 'Ee.current' as it is null.", // MUI Tabs related error, remove from list when use of MUI Tabs is removed
    'Non-Error promise rejection captured with value',
    'Right side of assignment cannot be destructured',
    'Ee.current is null',
    // api.ts sometimes throws bare `Error(response.problem)` rather than ApiRequestError, so these
    // apisauce problem codes need a message-based backstop here too
    'NETWORK_ERROR',
    'CONNECTION_ERROR',
    'TIMEOUT_ERROR'
  ];
  const logoutImportFailureMessages = [
    'Failed to fetch dynamically imported module', // This error occurs on user logout in Chromium-based browsers when a lazy-loaded route chunk is fetched while the browser is leaving the SPA.
    'Importing a module script failed.' // This error occur on user logout in Safari, likely due to the way Safari handles module scripts.
  ];
  const logoutBreadcrumbMessage = 'Sign out. Redirecting to login';

  // HTTP statuses that represent expected, user-facing outcomes (bad input, missing auth,
  // not found, rate limiting) rather than application bugs - not actionable in Sentry.
  const ignoredApiStatuses = new Set([401, 403, 404, 422, 429]);

  // apisauce problem codes for requests that never reached the server (offline, CORS,
  // dropped connection, timeout) - client connectivity issues, not application bugs.
  const ignoredApiProblems = new Set(['NETWORK_ERROR', 'CONNECTION_ERROR', 'TIMEOUT_ERROR']);

  const isIgnorableApiError = (error: ApiRequestError) => {
    if (error.problem !== undefined && ignoredApiProblems.has(error.problem)) {
      return true;
    }

    // Profile fetch after login is the outage the retry screen is for.
    if (error.context?.route === 'bruker') {
      return false;
    }

    return error.status !== undefined && ignoredApiStatuses.has(error.status);
  };

  // Portal API requests. They are traced by `traceApiRequests` (services/requestTracing.ts), one
  // trace per request, so Sentry's XHR instrumentation must not add spans of its own for them.
  const apiUrlPatterns = [/\/ems\/portal\/api(\/|$)/, /^https?:\/\/localhost(:\d+)?\/api/];

  Sentry.init({
    dsn: window.config.VITE_SENTRY_DSN,
    normalizeDepth: 5,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        shouldCreateSpanForRequest: (url) => !apiUrlPatterns.some((pattern) => pattern.test(url)),
        // Replaced by `trackPageTraces`, which links page traces to each other without the API calls in between.
        linkPreviousTrace: 'off'
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      }),
      // `setup` runs before browser tracing starts the pageload span, so the pageload is tracked too.
      { name: 'PageTraces', setup: trackPageTraces }
    ],
    sampleRate: 1,
    tracesSampleRate: 0.1,
    tracePropagationTargets: apiUrlPatterns,
    beforeSend(event, hint) {
      const error = hint.originalException;

      if (error instanceof ApiRequestError) {
        if (isIgnorableApiError(error)) {
          return null;
        }

        // Mirrors the tags/context react-query's QueryCache/MutationCache used to attach per call
        // site, applied here so every ApiRequestError is enriched consistently regardless of how
        // it was captured (react-query, or a manual Sentry.captureException elsewhere).
        event.tags = {
          ...event.tags,
          ...(error.errorCode ? { 'api.error_code': error.errorCode } : {}),
          ...(error.traceId ? { 'api.trace_id': error.traceId } : {}),
          ...(error.context?.registrationId ? { 'registration.id': error.context.registrationId } : {}),
          ...(error.context?.step ? { 'registration.step': error.context.step } : {}),
          ...(error.context?.route ? { route: error.context.route } : {})
        };

        event.contexts = {
          ...event.contexts,
          apiRequestError: {
            status: error.status,
            detail: error.detail,
            problem: error.problem,
            traceId: error.traceId,
            errorCode: error.errorCode,
            registrationId: error.context?.registrationId,
            step: error.context?.step,
            route: error.context?.route
          },
          // The error is usually captured after the request has finished, outside its trace. Point
          // the event at the request's own trace so Sentry shows it in the trace that matches the
          // backend's logs in LogAnalysis.
          ...(error.requestTrace
            ? { trace: { trace_id: error.requestTrace.traceId, span_id: error.requestTrace.spanId } }
            : {})
        };
      }

      if (error instanceof Error && errorsToExclude.includes(error.message)) {
        return null;
      }

      // During logout/session expiry the SPA may start loading a lazy route chunk while the browser is
      // already leaving the page. Ignore that import failure only when we can see the logout breadcrumb.
      if (
        error instanceof Error &&
        logoutImportFailureMessages.some((message) => error.message.includes(message)) &&
        event.breadcrumbs?.some((breadcrumb) => breadcrumb.message?.includes(logoutBreadcrumbMessage))
      ) {
        return null;
      }

      return event;
    }
  });
}
